import React from 'react';
import { NavLink } from 'react-router-dom';
import SiteContentContext from '../../content/SiteContentContext';
import { getActiveLocale, getByPath } from '../../content/siteContentUtils';

var bgimg = require('./../../images/background/cross-line2.png');

const fallbackFireDoors = [
  {
    image: require('./../../images/products/pic-3.jpg'),
    name: 'Sertifikalı Yangın Kapısı 60 DK',
    code: 'YGK-060',
    description: '60 dakika dayanımlı gövde yapısı ile acil çıkışlar için güvenli çözüm.',
  },
  {
    image: require('./../../images/products/pic-4.jpg'),
    name: 'Sertifikalı Yangın Kapısı 90 DK',
    code: 'YGK-090',
    description: 'Yüksek ısı yalıtımı ve sertifikalı kanat detayı ile güvenlik standardı sunar.',
  },
];

const fireDoorKeywords = ['yangin', 'fire'];

const normalizeSearchText = (value = '') =>
  String(value)
    .toLocaleLowerCase('tr-TR')
    .replace(/\u0131/g, 'i')
    .replace(/\u011f/g, 'g')
    .replace(/\u00fc/g, 'u')
    .replace(/\u015f/g, 's')
    .replace(/\u00f6/g, 'o')
    .replace(/\u00e7/g, 'c');

const normalizeCompareValue = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const getProductKey = (item = {}) => {
  const codePart = normalizeCompareValue(item.code);
  const namePart = normalizeCompareValue(item.name || item.title);
  return `${codePart}::${namePart}`;
};

const normalizeImageUrls = (item = {}, fallbackImage = '') => {
  const configuredImages = Array.isArray(item.imageUrls) ? item.imageUrls : [];
  const cleanedConfiguredImages = configuredImages
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean);

  if (cleanedConfiguredImages.length > 0) {
    return cleanedConfiguredImages;
  }

  const singleImage =
    (typeof item.imageUrl === 'string' && item.imageUrl.trim()) ||
    (typeof item.image === 'string' && item.image.trim()) ||
    '';

  if (singleImage) {
    return [singleImage];
  }

  return fallbackImage ? [fallbackImage] : [];
};

const normalizeProduct = (item = {}, fallback = {}) => {
  const imageUrls = normalizeImageUrls(item, fallback.image || '');
  const image = imageUrls[0] || fallback.image || '';

  return {
    image,
    imageUrls: imageUrls.length ? imageUrls : [image].filter(Boolean),
    name: item.name || item.title || fallback.name || 'Yangın Kapısı',
    code: item.code || fallback.code || '-',
    description: item.description || fallback.description || '',
  };
};

const isFireDoorProduct = (item = {}) => {
  const searchableText = normalizeSearchText(
    [item.name || item.title, item.description, item.code]
      .filter(Boolean)
      .join(' ')
  );

  return fireDoorKeywords.some((keyword) => searchableText.includes(keyword));
};

const getStaticCopy = (locale) => {
  if (locale === 'en') {
    return {
      title: 'Certified Fire Doors',
      subtitle: 'Fire safety doors',
      description:
        'We highlight fire-rated doors from the Interior Doors category with compliance-focused details and durable structure.',
      featureOne: 'Certified models aligned with current regulations',
      featureTwo: 'Production options for 60 and 90 minute resistance levels',
      featureThree: 'Fast support from product selection to installation',
      viewAllLabel: 'Explore Interior Doors',
      detailLabel: 'View product details',
      badge: 'Certified',
    };
  }

  return {
    title: 'Sertifikalı Yangın Kapıları',
    subtitle: 'Yangın güvenliği çözümü',
    description:
      'İç Kapılar kategorisindeki yangına dayanımlı ürünleri, yönetmelik odaklı detaylar ve dayanıklı gövde yapısıyla öne çıkarıyoruz.',
    featureOne: 'Yönetmeliklere uyumlu sertifikalı model seçenekleri',
    featureTwo: '60 ve 90 dakika dayanım seviyesine uygun üretim seçenekleri',
    featureThree: 'Keşif, ölçülendirme ve montaj sürecinde hızlı destek',
    viewAllLabel: 'İç Kapıları İncele',
    detailLabel: 'Ürün detayını gör',
    badge: 'Sertifikalı',
  };
};

class FireDoorHighlights extends React.Component {
  static contextType = SiteContentContext;

  getProductCollections(content) {
    const configuredInteriorProducts = getByPath(content, 'productCategories.interior.items', []);
    const sourceProducts = Array.isArray(configuredInteriorProducts) ? configuredInteriorProducts : [];

    const normalizedSourceProducts = sourceProducts.map((item, index) =>
      normalizeProduct(item, fallbackFireDoors[index % fallbackFireDoors.length])
    );

    const normalizedFallbackProducts = fallbackFireDoors.map((item) => normalizeProduct(item, item));
    const baseProducts = normalizedSourceProducts.length ? normalizedSourceProducts : normalizedFallbackProducts;
    const prioritizedProducts = baseProducts.filter(isFireDoorProduct);
    const selectedProducts = (prioritizedProducts.length ? prioritizedProducts : baseProducts).slice(0, 2);

    if (selectedProducts.length >= 2) {
      return {
        selectedProducts,
        relatedProducts: baseProducts,
      };
    }

    const selectedKeys = new Set(selectedProducts.map((item) => getProductKey(item)));
    const backupProducts = normalizedFallbackProducts.filter((item) => !selectedKeys.has(getProductKey(item)));

    return {
      selectedProducts: [...selectedProducts, ...backupProducts].slice(0, 2),
      relatedProducts: baseProducts.length ? baseProducts : normalizedFallbackProducts,
    };
  }

  render() {
    const content = this.context?.content || {};
    const locale = getActiveLocale(content);
    const copy = getStaticCopy(locale);
    const { selectedProducts, relatedProducts } = this.getProductCollections(content);

    return (
      <div className="section-full mobile-page-padding p-t80 p-b80 bg-white fire-door-highlight-section">
        <div className="container">
          <div className="row d-flex align-items-center">
            <div className="col-lg-4 col-md-12 m-b30">
              <div className="section-head m-b20">
                <div className="sx-separator-outer separator-left">
                  <div
                    className="sx-separator bg-white bg-moving bg-repeat-x"
                    style={{ backgroundImage: 'url(' + bgimg + ')' }}
                  >
                    <h3 className="sep-line-one">{copy.title}</h3>
                  </div>
                </div>
                <p className="fire-door-highlight-subtitle">{copy.subtitle}</p>
              </div>
              <p className="fire-door-highlight-description">{copy.description}</p>
              <ul className="fire-door-highlight-list">
                <li>
                  <i className="fa fa-check-circle" />
                  <span>{copy.featureOne}</span>
                </li>
                <li>
                  <i className="fa fa-check-circle" />
                  <span>{copy.featureTwo}</span>
                </li>
                <li>
                  <i className="fa fa-check-circle" />
                  <span>{copy.featureThree}</span>
                </li>
              </ul>
              <NavLink to="/ic-kapilar" className="site-button-secondry btn-half">
                <span>{copy.viewAllLabel}</span>
              </NavLink>
            </div>
            <div className="col-lg-8 col-md-12">
              <div className="row fire-door-highlight-grid">
                {selectedProducts.map((item, index) => {
                  const related = relatedProducts
                    .filter((product) => getProductKey(product) !== getProductKey(item))
                    .slice(0, 4)
                    .map((product) => ({
                      image: product.image,
                      imageUrls: product.imageUrls,
                      title: product.name,
                      code: product.code,
                      description: product.description,
                    }));

                  return (
                    <div className="col-md-6 m-b30" key={`${getProductKey(item)}-${index}`}>
                      <div className="fire-door-highlight-card">
                        <div className="fire-door-highlight-image-wrap">
                          <img src={item.image} alt={item.name} />
                          <span className="fire-door-highlight-badge">{copy.badge}</span>
                        </div>
                        <div className="fire-door-highlight-card-body">
                          <h4 className="fire-door-highlight-title">
                            <NavLink
                              to="/urun-detay"
                              state={{
                                categoryKey: 'interior',
                                product: {
                                  image: item.image,
                                  imageUrls: item.imageUrls,
                                  title: item.name,
                                  code: item.code,
                                  description: item.description,
                                },
                                relatedProducts: related,
                              }}
                            >
                              {item.name}
                            </NavLink>
                          </h4>
                          <p className="m-b10 product-code-text fire-door-highlight-code">{item.code || '-'}</p>
                          <p className="fire-door-highlight-card-description">{item.description}</p>
                          <NavLink
                            to="/urun-detay"
                            className="fire-door-highlight-detail-link"
                            state={{
                              categoryKey: 'interior',
                              product: {
                                image: item.image,
                                imageUrls: item.imageUrls,
                                title: item.name,
                                code: item.code,
                                description: item.description,
                              },
                              relatedProducts: related,
                            }}
                          >
                            {copy.detailLabel}
                            <i className="fa fa-long-arrow-right" />
                          </NavLink>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default FireDoorHighlights;
